import { Component, DestroyRef, computed, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { forkJoin } from 'rxjs';
import { AgGridAngular } from 'ag-grid-angular';
import {
  ColDef,
  ICellRendererParams,
  RowClassParams,
  RowClickedEvent,
  ValueFormatterParams,
  ValueGetterParams,
} from 'ag-grid-community';
import {
  HlmBtnDirective,
  HlmIconComponent,
  HlmInputDirective,
  HlmSpinnerComponent,
  ToastService,
} from '../../../ui';
import { PermissionService } from '../../../core/auth/permission.service';
import { BrandService } from '../brand.service';
import { Brand } from '../brand.model';
import { SubBrand } from '../domains/domain.model';
import { SubBrandService } from '../sub-brands/sub-brand.service';
import {
  SubBrandFormDialogComponent,
  SubBrandFormDialogData,
} from '../sub-brands/sub-brand-list/sub-brand-form-dialog.component';
import { getBrandLogoUrl } from '../brand-logo.util';
import { BrandLogoCellComponent } from './brand-logo-cell.component';
import { BrandRowNameCellComponent } from './brand-row-name-cell.component';
import { BrandListRow } from './brand-list-row.model';
import { BrandReadonlyBannerComponent } from '../brand-readonly-banner.component';

function statusDotCellRenderer(params: ICellRendererParams<BrandListRow>): HTMLElement | string {
  // Brand rows only — sub-brand rows render nothing in this column.
  if (params.data?.type !== 'brand') return '';
  const active = !!params.data?.active;
  const el = document.createElement('span');
  el.className = active ? 'cmt-brand-dot cmt-brand-dot-active' : 'cmt-brand-dot cmt-brand-dot-inactive';
  const title = active ? 'Active' : 'Inactive';
  el.setAttribute('aria-label', title);
  el.setAttribute('title', title);
  return el;
}

/** Brand-only string field for the grid — blank on sub-brand rows. */
function brandStringGetter(field: keyof BrandListRow) {
  return (params: ValueGetterParams<BrandListRow>): string => {
    const row = params.data;
    if (!row || row.type !== 'brand') return '';
    const value = row[field];
    return value == null ? '' : String(value);
  };
}

@Component({
  selector: 'cmt-brand-list',
  standalone: true,
  imports: [
    AgGridAngular,
    HlmBtnDirective,
    HlmIconComponent,
    HlmInputDirective,
    HlmSpinnerComponent,
    BrandReadonlyBannerComponent,
  ],
  templateUrl: './brand-list.component.html',
  styleUrl: './brand-list.component.scss',
})
export class BrandListComponent implements OnInit {
  private readonly brandService = inject(BrandService);
  private readonly subBrandService = inject(SubBrandService);
  private readonly router = inject(Router);
  private readonly toastService = inject(ToastService);
  private readonly permissionService = inject(PermissionService);
  private readonly dialog = inject(MatDialog);
  private readonly destroyRef = inject(DestroyRef);

  /** EB-3709: disable mutating actions for read-only users (no amie.brandManager.write). */
  protected readonly canWrite = computed(() => this.permissionService.canWrite('brandManager'));

  protected readonly getLogoUrl = (domain: string) => getBrandLogoUrl(domain, 'thumb');

  readonly loading = signal(false);
  /** Delegated to BrandService so the toggle survives edit/create navigation. */
  readonly hideInactive = this.brandService.hideInactive;
  // Raw signals — set from the parallel load; unified rows derived in computed (Pitfall #13).
  readonly brands = signal<Brand[]>([]);
  readonly subBrands = signal<SubBrand[]>([]);
  private readonly priceLists = signal<{ id: number; name: string }[]>([]);
  readonly filterText = signal('');

  readonly activeCount = computed(() => this.brands().filter(b => b.active).length);
  readonly inactiveCount = computed(() => this.brands().filter(b => !b.active).length);
  readonly subBrandCount = computed(() => this.subBrands().length);

  /**
   * Unified rows: each brand row, immediately followed by its sub-brands
   * (parentBrandId === brand.id) as indented child rows; orphan sub-brands at the
   * end. The "Hide inactive" toggle reduces brand rows; sub-brands always render
   * beneath a shown brand. The search filter matches brand name/domain AND
   * sub-brand name. Derived in a computed so it reacts to every source signal.
   */
  readonly rows = computed<BrandListRow[]>(() => {
    const q = this.filterText().trim().toLowerCase();
    const hideInactive = this.hideInactive();
    const allSubBrands = this.subBrands();

    let brands = this.brands();
    if (hideInactive) {
      brands = brands.filter(b => b.active);
    }
    // Default order: main brands by numeric id, sub-brands nested under their parent.
    const sortedBrands = [...brands].sort((a, b) => a.id - b.id);
    const knownBrandIds = new Set(sortedBrands.map(b => b.id));

    const brandMatches = (b: Brand): boolean =>
      !q ||
      (b.name?.toLowerCase().includes(q) ?? false) ||
      (b.domain?.toLowerCase().includes(q) ?? false) ||
      String(b.id) === q;

    const subBrandMatches = (s: SubBrand): boolean =>
      !q || (s.name?.toLowerCase().includes(q) ?? false);

    const toBrandRow = (b: Brand): BrandListRow => ({
      type: 'brand',
      id: b.id,
      name: b.name,
      domain: b.domain ?? undefined,
      defaultCountryName: b.defaultCountryName ?? undefined,
      defaultCityName: b.defaultCityName ?? undefined,
      currency: b.currency ?? undefined,
      active: b.active,
      logoDomain: b.domain ?? undefined,
      raw: b,
    });

    const toSubBrandRow = (s: SubBrand, parentName?: string, parentDomain?: string): BrandListRow => ({
      type: 'subbrand',
      id: s.id,
      name: s.name,
      parentBrandId: s.parentBrandId,
      parentName,
      parentDomain,
      raw: s,
    });

    const result: BrandListRow[] = [];

    for (const brand of sortedBrands) {
      const children = allSubBrands
        .filter(s => s.parentBrandId === brand.id)
        .filter(subBrandMatches)
        .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));

      // Keep the parent for context if it matches OR if any of its sub-brands match.
      if (!brandMatches(brand) && children.length === 0) continue;

      result.push(toBrandRow(brand));
      for (const child of children) {
        result.push(toSubBrandRow(child, brand.name, brand.domain ?? undefined));
      }
    }

    // Orphans: sub-brands with a null parent or a parent not in the (visible) brand set.
    const orphans = allSubBrands
      .filter(s => s.parentBrandId == null || !knownBrandIds.has(s.parentBrandId))
      .filter(subBrandMatches)
      .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));
    for (const orphan of orphans) {
      result.push(toSubBrandRow(orphan));
    }

    return result;
  });

  readonly columnDefs: ColDef<BrandListRow>[] = [
    {
      // Narrow dot column at the very start of the row — green = active, red = inactive.
      colId: 'status-dot',
      headerName: '',
      minWidth: 28,
      maxWidth: 32,
      cellRenderer: statusDotCellRenderer,
      sortable: false,
      filter: false,
      cellClass: 'cmt-brand-dot-cell',
      headerClass: 'cmt-brand-dot-header',
    },
    // ID column. Sort VALUE is numeric and group-aware: a brand sorts by its own
    // id; a sub-brand sorts by its PARENT's id so it stays nested with its main
    // brand instead of clumping at the top (orphans sort last). The DISPLAY is
    // blank for sub-brands — their id comes from a different table and showing it
    // beside brand ids is confusing. Within an equal-id group AG-Grid's stable
    // sort keeps the brand row before its sub-brands (rowData is pre-ordered).
    {
      colId: 'id',
      headerName: 'ID',
      minWidth: 60,
      maxWidth: 80,
      valueGetter: (p: ValueGetterParams<BrandListRow>): number =>
        p.data?.type === 'brand'
          ? p.data.id
          : (p.data?.parentBrandId ?? Number.MAX_SAFE_INTEGER),
      valueFormatter: (p: ValueFormatterParams<BrandListRow>) =>
        p.data?.type === 'brand' ? String(p.data.id) : '',
    },
    {
      headerName: '',
      minWidth: 72,
      maxWidth: 96,
      cellRenderer: BrandLogoCellComponent,
      sortable: false,
      filter: false,
      cellClass: 'cmt-brand-logo-cell-wrap',
    },
    // Name is the only stretchy column — absorbs all leftover horizontal space
    // so the row can't end short of the grid container (which would let the
    // row-hover tint paint into a blank strip on wide screens). Custom renderer
    // handles brand vs indented sub-brand display.
    {
      field: 'name',
      headerName: 'Name',
      minWidth: 220,
      flex: 1,
      cellRenderer: BrandRowNameCellComponent,
    },
    { headerName: 'Domain', minWidth: 200, cellClass: 'cmt-mono', valueGetter: brandStringGetter('domain') },
    { headerName: 'Country', minWidth: 120, maxWidth: 170, flex: 0.9, valueGetter: brandStringGetter('defaultCountryName') },
    { headerName: 'City', minWidth: 110, maxWidth: 150, flex: 0.8, valueGetter: brandStringGetter('defaultCityName') },
    { headerName: 'Currency', minWidth: 90, maxWidth: 110, cellClass: 'cmt-mono', valueGetter: brandStringGetter('currency') },
  ];

  readonly defaultColDef: ColDef = {
    sortable: true,
    resizable: true,
  };

  ngOnInit(): void {
    this.reload();
  }

  toggleHideInactive(value: boolean): void {
    this.hideInactive.set(value);
    // Filter is purely client-side now — no refetch needed.
  }

  reload(): void {
    this.loading.set(true);
    // Always fetch the full catalogue; the "Hide inactive" toggle filters
    // client-side. Brands + sub-brands + price lists loaded in parallel — the
    // last is needed to hand to the sub-brand dialog. Set raw signals only; the
    // unified row list is derived in `rows` (Pitfall #13).
    forkJoin({
      brands: this.brandService.listBrands(true),
      subBrands: this.subBrandService.listSubBrands(),
      priceLists: this.brandService.loadPriceLists(),
    }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: ({ brands, subBrands, priceLists }) => {
        this.brands.set(brands);
        this.subBrands.set(subBrands);
        this.priceLists.set(priceLists.map(p => ({ id: p.id, name: p.name })));
        this.loading.set(false);
      },
      error: () => {
        this.loading.set(false);
        this.toastService.open('Failed to load brands', 'OK', { duration: 5000 });
      },
    });
  }

  getRowClass(params: RowClassParams<BrandListRow>): string {
    const row = params.data;
    if (!row) return '';
    if (row.type === 'subbrand') return 'cmt-subbrand-row';
    // Explicit `false` only — undefined (backend hasn't added the field yet) stays active.
    return row.active === false ? 'cmt-brand-row-inactive' : '';
  }

  onRowClicked(event: RowClickedEvent<BrandListRow>): void {
    const row = event.data;
    if (!row) return;
    if (row.type === 'brand') {
      this.router.navigate(['/admin/brands', row.id, 'edit']);
    } else {
      this.openSubBrandDialog(row.raw as SubBrand);
    }
  }

  /** Card-mode click handler for a brand card — mirrors the grid's `rowClicked`. */
  onBrandCardClick(row: BrandListRow): void {
    this.router.navigate(['/admin/brands', row.id, 'edit']);
  }

  /** Card-mode click handler for a sub-brand sub-entry — opens the edit dialog. */
  onSubBrandClick(row: BrandListRow): void {
    this.openSubBrandDialog(row.raw as SubBrand);
  }

  /** Sub-brands shown under a given brand id, sorted by name (card mode). */
  subBrandsFor(brandId: number): BrandListRow[] {
    return this.rows().filter(r => r.type === 'subbrand' && r.parentBrandId === brandId);
  }

  /** Brand rows only (card mode iterates these). */
  readonly brandRows = computed(() => this.rows().filter(r => r.type === 'brand'));
  /** Orphan sub-brand rows (no matching/visible parent) — card mode appends these. */
  readonly orphanSubBrandRows = computed(() =>
    this.rows().filter(
      r => r.type === 'subbrand' && (r.parentBrandId == null || !this.brandRows().some(b => b.id === r.parentBrandId)),
    ),
  );

  /** New brand → dedicated full-page form. */
  newBrand(): void {
    if (!this.canWrite()) return;
    this.router.navigate(['/admin/brands/new']);
  }

  /** New sub-brand → dialog. Pre-selects a parent only if exactly one brand exists. */
  newSubBrand(): void {
    this.openSubBrandDialog(null);
  }

  private openSubBrandDialog(existing: SubBrand | null): void {
    if (!this.canWrite()) return;
    const brands = this.brands().map(b => ({ id: b.id, name: b.name, domain: b.domain ?? undefined }));
    const data: SubBrandFormDialogData = {
      existing,
      brands,
      priceLists: this.priceLists(),
    };
    this.dialog
      .open(SubBrandFormDialogComponent, {
        data,
        width: '520px',
        maxWidth: '92vw',
        autoFocus: false,
        panelClass: 'cmt-dialog-panel',
      })
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(saved => {
        if (saved) this.reload();
      });
  }

  readonly count = computed(() => this.brands().length);
  readonly visibleBrandCount = computed(() => this.brandRows().length);

  protected onHideInactiveChange(event: Event): void {
    const value = (event.target as HTMLInputElement).checked;
    this.toggleHideInactive(value);
  }
}
